'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useSession } from 'next-auth/react'
import { useRouter, usePathname } from 'next/navigation'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
} from '@mui/material'
import { TypographyVariants } from '@/app/styles/themes/typography'
import { AppConstants } from '@/lib/constants/constants'
import { pushToDataLayer } from '@/app/lib/analytics/analyticsTracking'
import { getEnvVariable } from '@/app/service/getenvService'
import { useUserContext } from '@/lib/context/userContext'

/**
 * InactivityMonitor - Logs out users after period of inactivity
 *
 * Uses react-idle-timer to track user activity and automatically log out
 * users who are inactive for INACTIVITY_TIMEOUT_SEC (currently 1 hour).
 *
 * Features:
 * - Tracks mouse, keyboard, scroll, and touch events
 * - Cross-tab synchronization (idle in one tab = idle in all tabs)
 * - Warning modal 30 seconds before timeout
 * - Throttled event detection (1 second)
 * - Analytics tracking for inactivity-based logouts
 *
 * WHEN THE TIMER IS ACTIVE:
 * ✅ ENABLED: User is authenticated AND not on auth pages (/auth/*)
 * ❌ DISABLED: User is unauthenticated OR on auth pages
 *
 * This prevents the timeout modal from appearing over login/logout pages.
 *
 * NextAuth handles all other session management:
 * - Session storage and encryption
 * - Token refresh via JWT callback (refreshes when < 5 min to expiry)
 * - Session expiration (maxAge: 8 hours)
 * - Server-side session validation
 */
export default function InactivityMonitor() {
	const { status } = useSession()
	const router = useRouter()
	const pathname = usePathname()
	const { autosignout } = useUserContext()
	const [showWarning, setShowWarning] = useState(false)
	const [countdown, setCountdown] = useState(
		AppConstants.SESSION_WARNING_BEFORE_TIMEOUT_SEC
	)

	// Inactivity timeout: configurable via NEXT_PUBLIC_INACTIVITY_TIMEOUT_SEC env var.
	// Defaults to 3600 (1 hour). Set to a lower value in .env.local for faster local testing.
	const [inactivityTimeoutSec, setInactivityTimeoutSec] = useState(3600)
	const [inactivityTimeoutMs, setInactivityTimeoutMs] = useState(3600 * 1000)

	useEffect(() => {
		const loadTimeoutConfig = async () => {
			const raw = await getEnvVariable('NEXT_PUBLIC_INACTIVITY_TIMEOUT_SEC')
			const parsed = raw !== null ? Number(raw) : null
			if (parsed !== null && !Number.isNaN(parsed) && parsed > 0) {
				setInactivityTimeoutSec(parsed)
				setInactivityTimeoutMs(parsed * 1000)
			}
		}
		loadTimeoutConfig()
	}, [])

	// Track when countdown started to lock it independent of activity
	const countdownStartTime = useRef<number | null>(null)

	// Don't render at all if not authenticated or on auth pages
	// This completely stops the timer rather than just disabling it
	const shouldBeActive = status === 'authenticated' && !pathname?.startsWith('/auth/')

	// Use refs so idle timer callbacks always read the latest values,
	// even if react-idle-timer holds a stale function reference internally.
	const statusRef = useRef(status)
	statusRef.current = status
	const pathnameRef = useRef(pathname)
	pathnameRef.current = pathname

	const handleOnPrompt = useCallback(() => {
		// Don't show warning if not authenticated
		if (statusRef.current !== 'authenticated') {
			return
		}

		// Don't show warning on auth pages
		if (pathnameRef.current?.startsWith('/auth/')) {
			return
		}

		countdownStartTime.current = Date.now()
		setShowWarning(true)
		setCountdown(AppConstants.SESSION_WARNING_BEFORE_TIMEOUT_SEC)
	}, [])

	const handleOnIdle = useCallback(() => {
		// Don't logout if not authenticated
		if (statusRef.current !== 'authenticated') {
			return
		}

		// Don't logout on auth pages
		if (pathnameRef.current?.startsWith('/auth/')) {
			return
		}

		// Track inactivity logout event for analytics
		pushToDataLayer({
			event: 'Session Expired',
			session_expired_reason: 'inactivity_timeout',
			session_expired_path: pathnameRef.current ?? '/',
			inactive_duration_seconds: inactivityTimeoutSec.toString(),
		})

		// Trigger NextAuth signout which will:
		// 1. Clear application session (NextAuth cookies)
		// 2. Trigger signOut event in auth.ts (clear custom cookies)
		// 3. Redirect to home page per signOut page config
		autosignout()
	}, [router, inactivityTimeoutSec])

	const { activate } = useIdleTimer({
		onIdle: handleOnIdle,
		onPrompt: handleOnPrompt,
		timeout: inactivityTimeoutMs,
		promptBeforeIdle: AppConstants.SESSION_WARNING_BEFORE_TIMEOUT_SEC * 1000,
		throttle: 1000,
		events: [
			'mousemove',
			'mousedown',
			'keydown',
			'scroll',
			'touchstart',
			'click',
		],
		disabled: !shouldBeActive, // Disable timer when not active
		crossTab: true, // Sync idle state across browser tabs
		syncTimers: 200, // Sync check interval in ms
	})

	// Close modal if user becomes unauthenticated or navigates to auth page
	useEffect(() => {
		if (!shouldBeActive && showWarning) {
			setShowWarning(false)
			countdownStartTime.current = null
		}
	}, [shouldBeActive, showWarning])

	// Update countdown while warning is shown - LOCKED (independent of activity)
	// Once the modal appears, the countdown continues regardless of user activity
	// This forces explicit acknowledgment via button click
	useEffect(() => {
		if (!showWarning || countdownStartTime.current === null) return

		const interval = setInterval(() => {
			const elapsed = Math.floor((Date.now() - countdownStartTime.current!) / 1000)
			const remaining = Math.max(0, AppConstants.SESSION_WARNING_BEFORE_TIMEOUT_SEC - elapsed)
			setCountdown(remaining)

			if (remaining <= 0) {
				clearInterval(interval)
				setShowWarning(false)
				countdownStartTime.current = null
			}
		}, 100)

		return () => clearInterval(interval)
	}, [showWarning])

	const handleStayLoggedIn = () => {
		setShowWarning(false)
		activate() // Reset the idle timer
		pushToDataLayer({
			event: 'Session Extended',
			session_extended_reason: 'user_action',
			session_extended_path: pathname ?? '/',
		})
	}

	const handleLogout = () => {
		setShowWarning(false)
		pushToDataLayer({
			event: 'Session Expired',
			session_expired_reason: 'user_logout',
			session_expired_path: pathname ?? '/',
		})

		// Call signout which will:
		// 1. Clear all storage (localStorage, sessionStorage)
		// 2. Invalidate NextAuth session (without redirecting)
		// 3. URL stays the same, showing Welcome page at current path
		// 4. User's current URL becomes the callback for re-auth
		autosignout()
	}

	// Don't render modal if not active or warning not showing
	if (!shouldBeActive || !showWarning) {
		return null
	}

	return (
		<Dialog
			open={showWarning}
			maxWidth="sm"
			fullWidth
			style={{ backgroundColor: '#40000000' }}
			data-testid="inactivity-warning-dialog"
			aria-labelledby="inactivity-dialog-title"
			aria-describedby="inactivity-dialog-description"
			sx={{
				'& .MuiDialog-paper': {
					p: { xs: 3, sm: '20px' },
					m: 0,
				},
			}}
		>
			<DialogTitle
				id="inactivity-dialog-title"
				variant={TypographyVariants.mediumSemiBold}
				sx={{
					p: 0,
					py: { xs: 1, sm: 0 },
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				⚠️ Session Timeout Warning
			</DialogTitle>
			<DialogContent
				sx={{
					p: 0,
					py: 2,
				}}
			>
				<Typography
					id="inactivity-dialog-description"
					variant={TypographyVariants.smallRegular}
					sx={{ color: theme => theme.palette.foreground.neutral.onSurface }}
				>
					You&apos;ve been inactive. You&apos;ll be logged out in{' '}
					<Box
						component="span"
						sx={{
							fontWeight: 600,
							color: theme => theme.palette.error.main,
						}}
					>
						{countdown}
					</Box>{' '}
					seconds.
				</Typography>
			</DialogContent>
			<DialogActions
				sx={{
					p: 0,
					pt: 2,
					display: 'flex',
					gap: 2,
					justifyContent: 'flex-end',
				}}
			>
				<Button
					onClick={handleLogout}
					variant="outlined"
					color="secondary"
					data-testid="inactivity-logout-button"
				>
					Logout
				</Button>
				<Button
					onClick={handleStayLoggedIn}
					variant="contained"
					color="primary"
					data-testid="inactivity-stay-button"
				>
					Stay Logged In
				</Button>
			</DialogActions>
		</Dialog>
	)
}
